import type {
  AutomationEvidence,
  CertificateEvidence,
  DecisionProof,
  DemoRunResult,
  DqReport,
  ScenarioDefinition,
  ReviewAction,
  ReviewCaseDetail,
  ReviewCaseSummary,
  ReviewResolveResponse,
  StepUpEvidence,
  TransferOptions,
  TransferSelection,
} from "./types";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const detail =
      (body as { detail?: string } | null)?.detail ?? `${response.status} ${response.statusText}`;
    throw new ApiError(response.status, detail);
  }
  return body as T;
}

export function fetchScenarios(): Promise<ScenarioDefinition[]> {
  return request<ScenarioDefinition[]>("/v1/demo/scenarios");
}

export function runScenario(scenarioId: string): Promise<DemoRunResult> {
  return request<DemoRunResult>(`/v1/demo/scenarios/${scenarioId}/runs`, { method: "POST" });
}

/**
 * Automation figures come from the committed evaluation artifact via the backend. A 404 here
 * means the artifact has not been generated, and the caller must show "Not measured yet"
 * rather than substituting a number of its own.
 */
export function fetchAutomationEvidence(): Promise<AutomationEvidence> {
  return request<AutomationEvidence>("/v1/evidence/automation");
}

/** The allowed choices come from the server, so the form cannot offer a value the API
 *  would reject with a 422. */
export function fetchTransferOptions(): Promise<TransferOptions> {
  return request<TransferOptions>("/v1/demo/transfer/options");
}

export function runTransfer(selection: TransferSelection): Promise<DemoRunResult> {
  return request<DemoRunResult>("/v1/demo/transfer", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(selection),
  });
}

/** Before/after human-intervention figures from `ml/AUTOMATION.md`'s measurement. 404
 *  means the artifacts have not been generated - the caller renders "Not measured yet". */
export function fetchStepUpEvidence(): Promise<StepUpEvidence> {
  return request<StepUpEvidence>("/v1/evidence/step-up");
}

/** The AI's driving licence: pre-measured limits of how strong an attacker the automation
 *  provably survives. 404 means `ml/certify_v3.py` has not produced the artifact - the
 *  caller renders "Not measured yet". */
export function fetchCertificateEvidence(): Promise<CertificateEvidence> {
  return request<CertificateEvidence>("/v1/evidence/certificate");
}

/** The second, separate request of the "10초 검증" flow: re-reads what PostgreSQL stored
 *  for a freshly issued decision_id. The UI must show storage confirmations only from
 *  this response - never by re-using the decision response it already has. */
export function fetchDecisionProof(decisionId: string): Promise<DecisionProof> {
  return request<DecisionProof>(`/v1/demo/decisions/${decisionId}/proof`);
}

export function fetchReviewQueue(): Promise<ReviewCaseSummary[]> {
  return request<ReviewCaseSummary[]>("/v1/demo/review/queue");
}

export function fetchReviewCase(caseId: string): Promise<ReviewCaseDetail> {
  return request<ReviewCaseDetail>(`/v1/demo/review/cases/${caseId}`);
}

export function resolveReviewCase(
  caseId: string,
  action: ReviewAction,
  reason: string,
): Promise<ReviewResolveResponse> {
  return request<ReviewResolveResponse>(`/v1/demo/review/cases/${caseId}/resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, reason }),
  });
}

/** The ledger's self-audit: are all decision records complete, balanced, timely, and
 *  cleared? Read-only projection of the live database (ADR-024). */
export function fetchDqReport(): Promise<DqReport> {
  return request<DqReport>("/v1/evidence/dq");
}
